import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Switch,
    Typography,
    Box,
    Divider,
    IconButton,
    Chip
} from '@mui/material';
import {
    Settings as SettingsIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

const SidebarCustomizationDialog = ({ open, onClose, menuGroups, onSave, initialPreferences }) => {
    const [preferences, setPreferences] = useState(initialPreferences || {});

    useEffect(() => {
        if (open) {
            setPreferences(initialPreferences || {});
        }
    }, [open, initialPreferences]);

    const handleToggleGroup = (groupLabel, groupItems) => {
        const isVisible = preferences[groupLabel] !== false;
        const newPrefs = { ...preferences, [groupLabel]: !isVisible };
        (groupItems || []).forEach((item) => {
            newPrefs[`${groupLabel}:${item.key}`] = !isVisible;
        });
        setPreferences(newPrefs);
    };

    const handleToggleItem = (groupLabel, itemKey) => {
        const itemVisibilityKey = `${groupLabel}:${itemKey}`;
        const isVisible = preferences[itemVisibilityKey] !== false;
        setPreferences({
            ...preferences,
            [itemVisibilityKey]: !isVisible
        });
    };

    const handleSave = () => {
        onSave(preferences);
        onClose();
    };

    const handleReset = () => {
        setPreferences({});
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VisibilityIcon color="primary" />
                    <Typography variant="h6">Customize Sidebar</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Toggle the visibility of sections and items in your sidebar. These settings will be saved to your organization profile.
                </Typography>
                <List sx={{ pt: 0 }}>
                    {menuGroups.map((group) => {
                        const isGroupVisible = preferences[group.label] !== false;
                        return (
                            <React.Fragment key={group.label}>
                                <ListItem
                                    sx={{
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                        mb: 1,
                                        mt: 1
                                    }}
                                >
                                    <ListItemText
                                        primary={group.label}
                                        primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 700, textTransform: 'uppercase' }}
                                    />
                                    <Switch
                                        checked={isGroupVisible}
                                        onChange={() => handleToggleGroup(group.label, group.items)}
                                        size="small"
                                    />
                                </ListItem>
                                <Box sx={{ pl: 4, opacity: isGroupVisible ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                                    {group.items.map((item) => {
                                        const itemVisibilityKey = `${group.label}:${item.key}`;
                                        const isItemVisible = preferences[itemVisibilityKey] !== false;
                                        return (
                                            <ListItem key={item.key} dense>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    {item.icon}
                                                </ListItemIcon>
                                                <ListItemText primary={item.text} />
                                                <Switch
                                                    checked={isItemVisible}
                                                    onChange={() => handleToggleItem(group.label, item.key)}
                                                    size="small"
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </Box>
                            </React.Fragment>
                        );
                    })}
                </List>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleReset} color="inherit">Reset to Default</Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save Preferences</Button>
            </DialogActions>
        </Dialog>
    );
};

export default SidebarCustomizationDialog;
